import { useEffect, useRef } from 'react'
import { HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr'
import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'
import { getApiBaseUrl, getRemoteHeartbeatIntervalMs, getSignalRReconnectDelays, delayForTailscaleStagger, withHeartbeatJitter } from '../services/apiConfig'
import { useAuthStore } from '../store/authStore'
import { mapFuelNotification, useFuelNotificationStore } from '../store/fuelNotificationStore'
import { playFuelStationSound } from '../services/fuelStationSound'
import { secureStorage } from '../utils/secureStorage'
import { useModuleStore } from '../store/moduleStore'

function hubUrl() {
  return getApiBaseUrl().replace(/\/api\/?$/, '') + '/hubs/fuel-station'
}

async function loadList() {
  const res = await apiClient.get(API_ENDPOINTS.fuelStation.notifications, { params: { take: 40 } })
  const rows = (res.data?.data || []).map(mapFuelNotification)
  const unread = Number(res.data?.unreadCount ?? rows.filter((x: { isRead: boolean }) => !x.isRead).length)
  useFuelNotificationStore.getState().hydrate(rows, unread)
}

export function useFuelStationRealtime() {
  const token = useAuthStore((s) => s.token)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const fuelEnabled = useModuleStore((s) => s.enableFuelStationModule)
  const seen = useRef(new Set<string>())

  useEffect(() => {
    if (!isAuthenticated || !token || !fuelEnabled) return
    let stopped = false
    let connection: ReturnType<typeof HubConnectionBuilder.prototype.build> | null = null
    let poll: ReturnType<typeof setInterval> | null = null

    const onEvent = (raw: any) => {
      const row = useFuelNotificationStore.getState().incoming(raw)
      if (!row || seen.current.has(row.notificationId)) return
      seen.current.add(row.notificationId)
      playFuelStationSound(row.soundType)
    }

    void (async () => {
      await delayForTailscaleStagger(400)
      if (stopped) return
      try {
        await loadList()
        useFuelNotificationStore.getState().items.forEach((x) => seen.current.add(x.notificationId))
      } catch {
        /* list will retry via poll */
      }

      const jwt = () => useAuthStore.getState().token || secureStorage.getItem('token') || ''
      connection = new HubConnectionBuilder()
        .withUrl(hubUrl(), {
          accessTokenFactory: jwt,
          withCredentials: false,
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect(getSignalRReconnectDelays())
        .configureLogging(LogLevel.None)
        .build()
      connection.on('fuelStationEvent', onEvent)
      try {
        await connection.start()
      } catch {
        /* fallback poll below */
      }

      let pollInflight = false
      poll = setInterval(async () => {
        if (stopped || pollInflight || document.hidden) return
        pollInflight = true
        try {
          const res = await apiClient.get(API_ENDPOINTS.fuelStation.notifications, {
            params: { take: 12, unreadOnly: true },
            timeout: 8000,
          })
          const rows = res.data?.data || []
          rows.forEach((raw: any) => onEvent(raw))
          if (typeof res.data?.unreadCount === 'number') {
            useFuelNotificationStore.setState({ unreadCount: res.data.unreadCount })
          }
        } catch {
          /* ignore */
        } finally {
          pollInflight = false
        }
      }, withHeartbeatJitter(getRemoteHeartbeatIntervalMs(60_000)))
    })()

    return () => {
      stopped = true
      if (poll) clearInterval(poll)
      if (connection) {
        connection.off('fuelStationEvent')
        void connection.stop()
      }
    }
  }, [isAuthenticated, token, fuelEnabled])
}
